import { ConvexError } from 'convex/values';
export type Plan = 'free' | 'team' | 'business';
export type CountLimitedPlanFeature =
  | 'clients'
  | 'products'
  | 'productLists'
  | 'invoicesPerMonth'
  | 'employees'
  | 'payslipsPerMonth'
  | 'aiPrefillsPerMonth'
  | 'recurringInvoiceSchedules'
  | 'supplierBillExtractionsPerMonth'
  | 'supplierBillsPerMonth'
  | 'bankTransactionsPerMonth'
  | 'invoiceImportDocuments';

export const PLAN_LIMITS = {
  free: {
    clients: 10,
    products: 5,
    productLists: 1,
    invoicesPerMonth: 20,
    employees: 3,
    payslipsPerMonth: 3,
    aiPrefillsPerMonth: 5,
    recurringInvoiceSchedules: 1,
    supplierBillExtractionsPerMonth: 10,
    supplierBillsPerMonth: 15,
    annualCertificate: false,
    financialStatements: false,
    // Accounting module, bank half: every plan imports and reconciles; the
    // free plan is metered on imported rows, AI categorisation is paid-only.
    bankReconciliation: true,
    bankTransactionsPerMonth: 100,
    aiCategorization: false,
    // Reminders sent on the owner's behalf. Every plan sends them by hand.
    autoReminders: false,
    // Invoice import (migration from another software): documents read by
    // the AI over the company's whole life, never reset. Upgrading raises it.
    invoiceImportDocuments: 50,
  },
  team: {
    clients: 25,
    products: Infinity,
    productLists: Infinity,
    invoicesPerMonth: 50,
    employees: 20,
    payslipsPerMonth: 20,
    aiPrefillsPerMonth: 20,
    recurringInvoiceSchedules: Infinity,
    supplierBillExtractionsPerMonth: 50,
    supplierBillsPerMonth: 75,
    annualCertificate: true,
    financialStatements: false,
    // Accounting module, bank half: every plan imports and reconciles; the
    // free plan is metered on imported rows, AI categorisation is paid-only.
    bankReconciliation: true,
    bankTransactionsPerMonth: Infinity,
    aiCategorization: true,
    autoReminders: true,
    invoiceImportDocuments: 500,
  },
  business: {
    clients: Infinity,
    products: Infinity,
    productLists: Infinity,
    invoicesPerMonth: Infinity,
    employees: Infinity,
    payslipsPerMonth: Infinity,
    aiPrefillsPerMonth: 100,
    recurringInvoiceSchedules: Infinity,
    supplierBillExtractionsPerMonth: 200,
    supplierBillsPerMonth: Infinity,
    annualCertificate: true,
    financialStatements: true,
    // Accounting module, bank half: every plan imports and reconciles; the
    // free plan is metered on imported rows, AI categorisation is paid-only.
    bankReconciliation: true,
    bankTransactionsPerMonth: Infinity,
    aiCategorization: true,
    autoReminders: true,
    invoiceImportDocuments: 2000,
  },
} as const;

export function isPlanLimitReached(
  plan: Plan,
  feature: CountLimitedPlanFeature,
  currentCount: number,
) {
  return currentCount >= PLAN_LIMITS[plan][feature];
}

function assertCanCreateWithinPlanLimit(
  plan: Plan,
  feature: CountLimitedPlanFeature,
  currentCount: number,
  errorCode: string,
) {
  if (isPlanLimitReached(plan, feature, currentCount)) {
    throw new Error(errorCode);
  }
}

export function assertCanCreateClient(plan: Plan, currentCount: number) {
  assertCanCreateWithinPlanLimit(
    plan,
    'clients',
    currentCount,
    'CLIENT_LIMIT_REACHED',
  );
}

export function assertCanCreateProduct(plan: Plan, currentCount: number) {
  assertCanCreateWithinPlanLimit(
    plan,
    'products',
    currentCount,
    'PRODUCT_LIMIT_REACHED',
  );
}

export function assertCanCreateProductList(plan: Plan, currentCount: number) {
  assertCanCreateWithinPlanLimit(
    plan,
    'productLists',
    currentCount,
    'PRODUCT_LIST_LIMIT_REACHED',
  );
}

export function assertCanCreateInvoice(plan: Plan, currentCount: number) {
  assertCanCreateWithinPlanLimit(
    plan,
    'invoicesPerMonth',
    currentCount,
    'INVOICE_LIMIT_REACHED',
  );
}

export function assertCanCreateEmployee(plan: Plan, currentCount: number) {
  assertCanCreateWithinPlanLimit(
    plan,
    'employees',
    currentCount,
    'EMPLOYEE_LIMIT_REACHED',
  );
}

export function assertCanCreatePayslip(plan: Plan, currentCount: number) {
  assertCanCreateWithinPlanLimit(
    plan,
    'payslipsPerMonth',
    currentCount,
    'PAYSLIP_LIMIT_REACHED',
  );
}

export function assertCanCreateRecurringInvoiceSchedule(
  plan: Plan,
  currentCount: number,
) {
  assertCanCreateWithinPlanLimit(
    plan,
    'recurringInvoiceSchedules',
    currentCount,
    'RECURRING_LIMIT_REACHED',
  );
}

export function assertCanUseAiPrefill(plan: Plan, currentCount: number) {
  assertCanCreateWithinPlanLimit(
    plan,
    'aiPrefillsPerMonth',
    currentCount,
    'AI_QUOTA_REACHED',
  );
}

export function assertCanUseSupplierBillExtraction(
  plan: Plan,
  currentCount: number,
) {
  assertCanCreateWithinPlanLimit(
    plan,
    'supplierBillExtractionsPerMonth',
    currentCount,
    'EXPENSE_AI_QUOTA_REACHED',
  );
}

export function assertCanCreateSupplierBill(plan: Plan, currentCount: number) {
  assertCanCreateWithinPlanLimit(
    plan,
    'supplierBillsPerMonth',
    currentCount,
    'SUPPLIER_BILL_LIMIT_REACHED',
  );
}

/**
 * The bank half of the accounting module — camt/CSV import, categorisation,
 * reconciliation — is open to every plan (free is metered on imported rows,
 * see `assertCanImportBankTransactions`). Kept as an explicit gate so the
 * split from `assertCanUseFinancialStatements` is visible at every call site.
 */
export function assertCanUseBankReconciliation(plan: Plan) {
  if (!PLAN_LIMITS[plan].bankReconciliation) {
    throw new Error('BANK_RECONCILIATION_UNAVAILABLE');
  }
}

/** `rows` about to be imported on top of `currentCount` this month. */
export function assertCanImportBankTransactions(
  plan: Plan,
  currentCount: number,
  rows: number,
) {
  if (currentCount + rows > PLAN_LIMITS[plan].bankTransactionsPerMonth) {
    throw new Error('BANK_TRANSACTION_LIMIT_REACHED');
  }
}

/**
 * Lifetime quota of the invoice import: `requested` more documents read by
 * the AI on top of the `used` ones (`companies.invoiceImportDocumentsUsed`).
 * Read with `getModulePlan(company, 'invoicing')`.
 */
export function assertCanImportInvoiceDocuments(
  plan: Plan,
  used: number,
  requested: number,
) {
  if (used + requested > PLAN_LIMITS[plan].invoiceImportDocuments) {
    throw new Error('INVOICE_IMPORT_QUOTA_REACHED');
  }
}

/** AI categorisation of bank rows costs a model call: paid plans only. */
export function assertCanUseAiCategorization(plan: Plan) {
  if (!PLAN_LIMITS[plan].aiCategorization) {
    throw new Error('AI_CATEGORIZATION_PAID_ONLY');
  }
}

/** Automatic invoice reminders: read with `getModulePlan(company, 'invoicing')`. */
export function assertCanUseAutoReminders(plan: Plan) {
  if (!PLAN_LIMITS[plan].autoReminders) {
    // A ConvexError keeps its code through production's message redaction.
    throw new ConvexError('AUTO_REMINDERS_PAID_ONLY');
  }
}

/**
 * The ledger-derived annual statement — the Business-only half of the
 * accounting module.
 */
export function assertCanUseFinancialStatements(plan: Plan) {
  if (!PLAN_LIMITS[plan].financialStatements) {
    // A ConvexError keeps its code through production's message redaction,
    // so the app can show the Business upsell instead of "Server Error".
    throw new ConvexError('FINANCIAL_STATEMENTS_PRO_ONLY');
  }
}
